import {
  type HostMessage,
  type SdkMessage,
  type GamePlayer,
  type PlayerDataUpdate,
  doesBrowserSupportSMSLinks,
  smsLink,
  assertUnreachable,
} from "@textclub/common";
import type { IFrameChildBridge } from "./bridge";

const MOCK_PLAYER_STORAGE_KEY = "txtclub_mock_player";
const MOCK_NOTIFICATIONS_KEY = "txtclub_mock_notifications";

function generatePlayerId(): string {
  return "mock-" + Math.random().toString(36).substring(2, 15);
}

interface V1MockNotification {
  message: string;
  attemptPushNotification: boolean;
  scheduledAt: string;
  identifier?: string;
  createdAt: string;
}

interface V2MockNotification {
  body: string;
  fallbackBody: string;
  ctaText: string;
  image: string;
  scheduledAt: string;
  identifier?: string;
  createdAt: string;
}

type MockNotification = V1MockNotification | V2MockNotification;

function parseEntryPayload(): Record<string, unknown> {
  console.log(window.location.search);
  const entryPayload = new URLSearchParams(window.location.search).get(
    "entryPayload",
  );
  if (typeof entryPayload === "string") {
    try {
      return JSON.parse(entryPayload) as Record<string, unknown>;
    } catch (e) {
      alert("Entry payload set but not valid JSON. Please check the URL.");
      console.error("Invalid entry payload JSON:", e);
      return {};
    }
  }
  return {};
}

function storePlayer(player: GamePlayer) {
  localStorage.setItem(
    MOCK_PLAYER_STORAGE_KEY,
    JSON.stringify({
      ...player,
      entryPayload: undefined,
    }),
  );
}

function getStoredPlayer(): GamePlayer | null {
  const storedPlayer = localStorage.getItem(MOCK_PLAYER_STORAGE_KEY);
  if (!storedPlayer) {
    return null;
  }
  return JSON.parse(storedPlayer) as GamePlayer;
}

function loadGamePlayer(): GamePlayer {
  // Check if we have the mock_login parameter in URL
  const urlParams = new URLSearchParams(window.location.search);
  const mockLogin = urlParams.get("mock_login") === "true";

  const storedPlayer = getStoredPlayer();
  if (!storedPlayer) {
    return {
      playerId: generatePlayerId(),
      registered: mockLogin, // Set registered to true if mock_login param exists
      playerData: {},
      entryPayload: parseEntryPayload(),
    };
  }

  const player = storedPlayer;

  player.entryPayload = parseEntryPayload();

  // Override registration status if mock_login parameter is present
  if (mockLogin) {
    player.registered = true;
    // Store the updated player to persist registration
    storePlayer(player);

    // Remove the mock_login parameter from URL to prevent reregistration on page refresh
    if (window.history && window.history.replaceState) {
      urlParams.delete("mock_login");
      const newUrl =
        window.location.pathname +
        (urlParams.toString() ? `?${urlParams.toString()}` : "") +
        window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
    }
  }

  return player;
}

export class MockBridge
  implements Pick<IFrameChildBridge, "onMessage" | "sendMessage">
{
  public onMessage: (message: SdkMessage) => void = () => {};
  private messageLog: HostMessage[] = [];
  private mockPlayer: GamePlayer;
  private onNotificationChange?: () => void;
  private onStateChange?: () => void;

  // Toggles.
  private rejectLoginLeaseAcquisition: boolean = false;
  private skipLoginSms: boolean = false;

  constructor() {
    // Load stored mock player or use default (unregistered)
    this.mockPlayer = loadGamePlayer();
    // Save initial state
    storePlayer(this.mockPlayer);
  }

  // Since this is a mock bridge, sendMessage is actually "receiving" messages from the client sdk, and should respond by calling onMessage directly
  sendMessage(message: HostMessage) {
    this.messageLog.push(message);
    console.log("[MockBridge] Received message:", message);
    // make it async, the send-receive should not be sync
    setTimeout(() => this.receiveMessage(message), 1);
  }

  private receiveMessage(message: HostMessage) {
    // Handle each message type exhaustively
    switch (message.type) {
      case "BeginPlatformLogin":
        this.handleBeginLogin(message);
        break;
      case "AcquireLoginLease":
        this.handleAcquireLoginLease(message);
        break;
      case "ScheduleNotification":
      case "ScheduleNotificationV2":
        this.handleNotification(message);
        break;
      case "UnscheduleNotification":
        this.handleUnschedule(message);
        break;
      case "UpdatePlayerData":
        this.handleUpdatePlayerData(message);
        break;
      case "RedirectToGame":
        this.handleRedirectToGame(message);
        break;
      case "OpenLegalPage":
        this.handleOpenLegalPage(message);
        break;
      case "Initialized":
        this.onMessage({
          type: "SetPlayer",
          player: this.mockPlayer,
        });
        break;
      case "CaptureEvent":
        console.log(`[Analytics Event] ${message.event}`, message.properties);
        break;
      case "CreateLink":
        this.handleGetLink(message);
        break;
      case "OnboardingCaptureEvent":
        console.log(`[Analytics Onboarding Event] ${message.event}`);
        break;
      case "BeginPlatformPurchase":
        console.log(
          `[MockBridge] BeginPlatformPurchase with productId: ${message.productSku}`,
        );
        break;
      case "GetGameProducts":
        console.log("[MockBridge] GetGameProducts called");
        break;
      default:
        // Type checking to ensure we're handling all message types
        assertUnreachable(message);
    }
  }

  private handleBeginLogin(
    message: Extract<HostMessage, { type: "BeginPlatformLogin" }>,
  ) {
    console.log(
      `[MockBridge] BeginPlatformLogin with entryPayload: ${message.entryPayload}`,
    );
    this.setMockPlayer({ registered: true });
  }

  private handleGetLink(message: Extract<HostMessage, { type: "CreateLink" }>) {
    console.log(`[MockBridge] GetLink for slug: ${message.gameSlug}`);
    const link = new URL(window.location.href);
    link.pathname = "/g/" + message.gameSlug;
    if (message.entryPayload) {
      link.searchParams.set("entryPayload", message.entryPayload);
    }
    this.onMessage({
      type: "CreateLinkResponse",
      success: true,
      link: link.toString(),
    });
  }

  private handleAcquireLoginLease(
    message: Extract<HostMessage, { type: "AcquireLoginLease" }>,
  ) {
    console.log(
      `[MockBridge] AcquireLoginLease with message: ${message.message}`,
    );

    if (this.rejectLoginLeaseAcquisition) {
      console.log("[MockBridge] Rejecting login lease acquisition");
      this.onMessage({
        type: "LoginLeaseAcquired",
        success: false,
        error: "internal_error",
        link: "",
      });
      return;
    }

    let link: string;

    // If Skip login SMS is enabled, skip SMS preview and set mock_login directly
    if (this.skipLoginSms) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set("mock_login", "true");
      console.log("[MockBridge] Skipping SMS and setting mock_login directly");
      link = currentUrl.toString();
    } else {
      // Default SMS flow when Skip login SMS is OFF
      // Do we support sms links?
      if (doesBrowserSupportSMSLinks({ userAgent: navigator.userAgent })) {
        link = smsLink("5555555555", message.message);
      } else {
        // Generate a link based on current URL with a mock_login parameter
        const escapedMessage = message.message
          .replace(/"/g, '\\"')
          .replace(/'/g, "\\'");

        link = `javascript:alert('Sending SMS with text: ${escapedMessage}')`;
      }
    }

    this.onMessage({
      type: "LoginLeaseAcquired",
      success: true,
      link: link,
      conversationId: message.conversationId,
    });
  }

  private handleUpdatePlayerData(
    message: Extract<HostMessage, { type: "UpdatePlayerData" }>,
  ) {
    console.log(`[MockBridge] Updating player state with ID: ${message.id}`);
    this.updatePlayerData(message.update);
    // Send acknowledgment immediately
    this.onMessage({
      type: "AckUpdatePlayerData",
      id: message.id,
    });

    // Notify UI to refresh
    this.onStateChange?.();
  }

  private handleRedirectToGame(
    message: Extract<HostMessage, { type: "RedirectToGame" }>,
  ) {
    console.log(
      `[MockBridge] Redirecting to full game with payload: ${message.entryPayload}`,
    );

    // Create an overlay to dim the background
    const overlay = document.createElement("div");
    overlay.style.position = "fixed";
    overlay.style.top = "0";
    overlay.style.left = "0";
    overlay.style.width = "100%";
    overlay.style.height = "100%";
    overlay.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
    overlay.style.zIndex = "9998";
    document.body.appendChild(overlay);

    // Create a modal dialog that shows the redirect instead of actually redirecting
    const redirectBox = document.createElement("div");
    redirectBox.style.position = "fixed";
    redirectBox.style.top = "50%";
    redirectBox.style.left = "50%";
    redirectBox.style.transform = "translate(-50%, -50%)";
    redirectBox.style.backgroundColor = "#ffffff";
    redirectBox.style.color = "#333333";
    redirectBox.style.padding = "30px";
    redirectBox.style.borderRadius = "8px";
    redirectBox.style.boxShadow = "0 4px 16px rgba(0,0,0,0.3)";
    redirectBox.style.zIndex = "9999";
    redirectBox.style.width = "400px";
    redirectBox.style.maxWidth = "90%";
    redirectBox.style.fontFamily = "sans-serif";
    redirectBox.style.textAlign = "center";

    // Prepare the entryPayload display HTML if it exists
    let entryPayloadHtml = "";

    if (message.entryPayload) {
      let payloadDisplay = "";
      try {
        // If it's a string (JSON), parse and then format it
        if (typeof message.entryPayload === "string") {
          const parsed = JSON.parse(message.entryPayload) as Record<
            string,
            unknown
          >;
          payloadDisplay = JSON.stringify(parsed, null, 2);
        } else {
          // Otherwise just stringify the object
          payloadDisplay = JSON.stringify(message.entryPayload, null, 2);
        }
      } catch (_e) {
        // If parsing fails, just use the raw string
        payloadDisplay = message.entryPayload;
      }

      entryPayloadHtml = `
        <div style="margin-top: 20px;">
          <h3 style="color: #4361EE; margin-bottom: 10px; font-size: 16px;">Entry Payload:</h3>
          <div style="background: #f0f8ff; padding: 15px; border-radius: 5px; margin: 10px 0; border-left: 4px solid #4361EE; text-align: left; overflow: auto; max-height: 150px;">
            <pre style="margin: 0; white-space: pre-wrap; font-family: monospace; font-size: 14px;">${payloadDisplay}</pre>
          </div>
        </div>
      `;
    }

    const redirectToTarget =
      "redirectToFlagship" in message
        ? "Redirecting to flagship game..."
        : "Redirecting to game " + message.gameSlug + "...";

    redirectBox.innerHTML = `
      <h2 style="margin-top: 0; color: #4CAF50;">${redirectToTarget}</h2>
      <p style="font-size: 16px; line-height: 1.5; margin: 20px 0;">
        You are now being redirected to the full game
      </p>
      ${entryPayloadHtml}
      <p style="font-size: 14px; opacity: 0.7; margin-top: 20px;">
        In a real TextClub environment, this page would automatically navigate away.
        <br>This message is only shown in development mode.
      </p>
    `;

    document.body.appendChild(redirectBox);
  }

  private handleOpenLegalPage(
    message: Extract<HostMessage, { type: "OpenLegalPage" }>,
  ) {
    console.log(`[MockBridge] Opening legal page: ${message.page}`);

    // Create an overlay to dim the background
    const overlay = document.createElement("div");
    overlay.style.position = "fixed";
    overlay.style.top = "0";
    overlay.style.left = "0";
    overlay.style.width = "100%";
    overlay.style.height = "100%";
    overlay.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
    overlay.style.zIndex = "9998";
    document.body.appendChild(overlay);

    // Get page title based on page type
    let pageTitle = "";
    switch (message.page) {
      case "privacy":
        pageTitle = "Privacy Policy";
        break;
      case "terms":
        pageTitle = "Terms of Service";
        break;
      case "copyright":
        pageTitle = "Copyright";
        break;
    }

    // Create a modal dialog that shows what would open in a new tab
    const legalPageBox = document.createElement("div");
    legalPageBox.style.position = "fixed";
    legalPageBox.style.top = "50%";
    legalPageBox.style.left = "50%";
    legalPageBox.style.transform = "translate(-50%, -50%)";
    legalPageBox.style.backgroundColor = "#ffffff";
    legalPageBox.style.color = "#333333";
    legalPageBox.style.padding = "30px";
    legalPageBox.style.borderRadius = "8px";
    legalPageBox.style.boxShadow = "0 4px 16px rgba(0,0,0,0.3)";
    legalPageBox.style.zIndex = "9999";
    legalPageBox.style.width = "400px";
    legalPageBox.style.maxWidth = "90%";
    legalPageBox.style.fontFamily = "sans-serif";
    legalPageBox.style.textAlign = "center";

    legalPageBox.innerHTML = `
      <h2 style="margin-top: 0; color: #4361EE;">${pageTitle}</h2>
      <p style="font-size: 16px; line-height: 1.5; margin: 20px 0;">
        A new browser tab would open with the platforms ${pageTitle} page:
      </p>
      <div style="background: #f5f5f5; padding: 15px; border-radius: 5px; margin: 20px 0; border-left: 4px solid #4361EE;">
        <code style="font-size: 18px; font-weight: bold;">/${message.page}</code>
      </div>
      <p style="font-size: 14px; opacity: 0.7;">
        In a real TextClub environment, this page would open in a new browser tab.
        <br>This message is only shown in development mode.
      </p>
      <button id="close-legal-modal" style="background-color: #4361EE; color: white; border: none; padding: 10px 20px; border-radius: 5px; cursor: pointer; margin-top: 15px;">Close</button>
    `;

    document.body.appendChild(legalPageBox);

    // Add event listener to close button
    const closeButton = document.getElementById("close-legal-modal");
    if (closeButton) {
      closeButton.addEventListener("click", () => {
        document.body.removeChild(overlay);
        document.body.removeChild(legalPageBox);
      });
    }
  }

  private handleNotification(
    message:
      | Extract<HostMessage, { type: "ScheduleNotification" }>
      | Extract<HostMessage, { type: "ScheduleNotificationV2" }>,
  ) {
    const notifications = this.getStoredNotifications();
    const newNotification: MockNotification =
      message.type === "ScheduleNotificationV2"
        ? {
            body: message.body,
            fallbackBody: message.fallbackBody,
            ctaText: message.ctaText,
            image: message.image,
            scheduledAt: message.scheduledAt,
            identifier: message.identifier,
            createdAt: new Date().toISOString(),
          }
        : {
            message: message.message,
            scheduledAt: message.scheduledAt,
            attemptPushNotification: message.attemptPushNotification,
            identifier: message.identifier,
            createdAt: new Date().toISOString(),
          };

    if (message.identifier) {
      const index = notifications.findIndex(
        (n) => n.identifier === message.identifier,
      );
      if (index !== -1) {
        notifications[index] = newNotification;
      } else {
        notifications.push(newNotification);
      }
    } else {
      notifications.push(newNotification);
    }

    localStorage.setItem(MOCK_NOTIFICATIONS_KEY, JSON.stringify(notifications));
    this.onNotificationChange?.();
  }

  private handleUnschedule(
    message: Extract<HostMessage, { type: "UnscheduleNotification" }>,
  ) {
    const notifications = this.getStoredNotifications().filter(
      (n) => n.identifier !== message.identifier,
    );
    localStorage.setItem(MOCK_NOTIFICATIONS_KEY, JSON.stringify(notifications));
    this.onNotificationChange?.();
  }

  getStoredNotifications(): MockNotification[] {
    const stored = localStorage.getItem(MOCK_NOTIFICATIONS_KEY);
    return stored ? (JSON.parse(stored) as MockNotification[]) : [];
  }

  clearNotifications() {
    localStorage.setItem(MOCK_NOTIFICATIONS_KEY, JSON.stringify([]));
    this.onNotificationChange?.();
  }

  // Debug API for game developers
  resetPlayer() {
    localStorage.removeItem(MOCK_PLAYER_STORAGE_KEY);
    localStorage.removeItem(MOCK_NOTIFICATIONS_KEY);
    this.messageLog = [];

    // Create new unregistered player
    this.mockPlayer = {
      playerId: generatePlayerId(),
      registered: false,
      entryPayload: {},
      playerData: {},
    };

    // Save the unregistered state
    storePlayer(this.mockPlayer);

    this.onMessage({
      type: "SetPlayer",
      player: { ...this.mockPlayer },
    });

    setTimeout(() => window.location.reload(), 50);
  }

  setMockPlayer(player: Partial<GamePlayer>) {
    // Allow manual ID changes now
    this.mockPlayer = { ...this.mockPlayer, ...player };
    storePlayer(this.mockPlayer);

    // Reload if critical properties changed
    if (
      "playerId" in player ||
      "registered" in player ||
      "entryPayload" in player
    ) {
      setTimeout(() => window.location.reload(), 50);
    }
  }

  setOnNotificationChange(callback: () => void) {
    this.onNotificationChange = callback;
  }

  setOnStateChange(callback: () => void) {
    this.onStateChange = callback;
  }

  getMockPlayer(): GamePlayer {
    return { ...this.mockPlayer };
  }

  getMessageLog(): HostMessage[] {
    return [...this.messageLog];
  }

  clearMessageLog() {
    this.messageLog = [];
  }

  updatePlayerData(update: PlayerDataUpdate) {
    this.setMockPlayer({
      playerData: { ...this.mockPlayer.playerData, ...update.update },
    });
    this.onStateChange?.();
  }

  getRejectLoginLeaseAcquisition() {
    return this.rejectLoginLeaseAcquisition;
  }

  setRejectLoginLeaseAcquisition(value: boolean) {
    this.rejectLoginLeaseAcquisition = value;
  }

  getSkipLoginSms() {
    return this.skipLoginSms;
  }

  setSkipLoginSms(value: boolean) {
    this.skipLoginSms = value;
  }
}
